import type { LucideIcon } from 'lucide-react'
import {
  BarChart3,
  Building2,
  FileSignature,
  FileUp,
  Footprints,
  Gauge,
  Home,
  Inbox,
  Mail,
  DoorOpen,
  Map,
  Megaphone,
  Settings,
  MessageSquareText,
  Target,
  Signpost,
  Users,
  UsersRound,
  Wallet,
} from 'lucide-react'
import type { Municipio, Permiso } from '@/api/modelos'
import { tieneAlgunPermiso } from '@/sesion/permisos'

export type Seccion = {
  /** Ruta relativa dentro de /m/:municipio. La de Inicio es la vacia. */
  ruta: string
  titulo: string
  icono: LucideIcon
  /** Basta uno para verla. Sin permisos, la ve cualquiera con sesion. */
  permisos?: readonly Permiso[]
}

/**
 * Las secciones del panel. Los permisos de aca solo OCULTAN: el servidor
 * autoriza igual en cada endpoint, asi que ver el menu nunca es poder hacer.
 */
export const SECCIONES: readonly Seccion[] = [
  { ruta: '', titulo: 'Inicio', icono: Home },
  { ruta: 'cartera', titulo: 'Cartera', icono: Wallet, permisos: ['cartera.ver'] },
  { ruta: 'mapa', titulo: 'Mapa', icono: Map, permisos: ['cartera.ver'] },
  { ruta: 'calles', titulo: 'Calles', icono: Signpost, permisos: ['cartera.ver'] },
  {
    ruta: 'contribuyentes',
    titulo: 'Contribuyentes',
    icono: UsersRound,
    permisos: ['cartera.ver'],
  },
  // La cartera y los pagos que entrega el municipio, cargados sin consola.
  {
    ruta: 'cargas',
    titulo: 'Cargas',
    icono: FileUp,
    permisos: ['cartera.importar', 'pagos.registrar'],
  },
  { ruta: 'segmentos', titulo: 'Segmentos', icono: Target, permisos: ['campanas.ver'] },
  {
    ruta: 'plantillas',
    titulo: 'Plantillas',
    icono: MessageSquareText,
    permisos: ['campanas.ver'],
  },
  { ruta: 'campanas', titulo: 'Campañas', icono: Megaphone, permisos: ['campanas.ver'] },
  // Solo la plataforma: el ritmo de salida y la reputacion (09-oct-2026).
  {
    ruta: 'control-envios',
    titulo: 'Control de envíos',
    icono: Gauge,
    permisos: ['envios.controlar'],
  },
  // La carta certificada por Correos: una campaña en papel, que se imprime por lotes.
  { ruta: 'cartas', titulo: 'Cartas', icono: Mail, permisos: ['campanas.ver'] },
  // Lo que contestan los vecinos a las campañas, y lo que el asistente derivo
  // a una persona (F4-03). Justo despues de Campañas porque es su vuelta.
  { ruta: 'bandeja', titulo: 'Bandeja', icono: Inbox, permisos: ['conversaciones.ver'] },
  // El gestor NO la ve, y no es un olvido: su ruta la recoge desde la app con
  // gestiones.registrar, y este tablero es el de la comuna entera.
  { ruta: 'rutas', titulo: 'Rutas', icono: Footprints, permisos: ['rutas.ver'] },
  // Lo que hizo el equipo en la calle, con su evidencia (F7-17). Junto a Rutas
  // porque es la otra mitad: la ruta dice a donde ir, las visitas que paso.
  { ruta: 'visitas', titulo: 'Visitas', icono: DoorOpen, permisos: ['cartera.ver'] },
  {
    ruta: 'convenios',
    titulo: 'Convenios',
    icono: FileSignature,
    permisos: ['convenios.gestionar', 'convenios.aprobar'],
  },
  { ruta: 'reportes', titulo: 'Reportes', icono: BarChart3, permisos: ['reportes.ver'] },
  // En el MENU va con configuracion.editar --es la pantalla del rol
  // municipio-- pero la PANTALLA se abre con cartera.ver y en solo lectura: la
  // API deja leer el perfil a cualquiera que vea la cartera, porque un gestor
  // tiene que poder decirle a un vecino donde paga. Un enlace directo a
  // /configuracion no le responde "no tienes permiso" a quien si puede leerlo.
  {
    ruta: 'configuracion',
    titulo: 'Configuración',
    icono: Settings,
    permisos: ['configuracion.editar'],
  },
  { ruta: 'usuarios', titulo: 'Usuarios', icono: Users, permisos: ['usuarios.administrar'] },
  // La ultima porque es la de la plataforma y no la del municipio: dar de alta
  // una comuna se hace una vez, y desde la comuna en la que ya estas.
  {
    ruta: 'municipios',
    titulo: 'Municipios',
    icono: Building2,
    permisos: ['municipios.administrar'],
  },
]

export function seccionesVisibles(municipio: Municipio | null): Seccion[] {
  return SECCIONES.filter(
    (seccion) => seccion.permisos === undefined || tieneAlgunPermiso(municipio, seccion.permisos),
  )
}

export function rutaDeSeccion(slug: string, ruta: string): string {
  return ruta === '' ? `/m/${slug}` : `/m/${slug}/${ruta}`
}
